import { For, Show } from 'solid-js';
import { Title } from '@solidjs/meta';
import { Heading, Container, Page, Section, HorizontalRule, Link, Hero } from '@troon/ui';
import { createAsync, useNavigate } from '@solidjs/router';
import { cachedGet } from '@troon/api-client';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { IconAccessPointsCustom } from '@troon/icons/access-points';
import { IconAccessRatesCustom } from '@troon/icons/access-rates';
import { SearchLocations } from '../../../components/search-locations';
import { PopularDestinationFullBleed } from '../../../components/popular-destination';
import { FacilitiesByGrouping } from '../../../components/facility/by-grouping';
import type { RouteDefinition } from '@solidjs/router';

export default function CourseDirectorty() {
	const navigate = useNavigate();

	const popularDesinations = createAsync(() => getPopular({}), { deferStream: true });
	const facilities = createAsync(
		async () => {
			const all = await getFacilities({
				query: { isBookable: true, sortBy: 'name' },
			});

			return all.facilities;
		},
		{ deferStream: true },
	);

	return (
		<>
			<Title>Explore Golf Courses | Golf with Access</Title>
			<Hero
				src="https://images.ctfassets.net/rdsy7xf5c8dt/6wmpgfIscFhZsfeIHC5AnV/286134ac72dc54a514fa0a517dc482e1/40df0b57edf404f46234faa4823ca852.jpg"
				fullWidth
				size="large"
			>
				<div class="flex max-w-3xl flex-col gap-8">
					<div class="flex flex-col gap-4">
						<Heading as="h1">Explore Courses</Heading>
						<p>
							Explore hundreds of premier golf courses across the country. Book your tee time with exclusive access to
							the best courses.
						</p>
					</div>
					<div class="w-full rounded-lg bg-white text-neutral-900">
						<SearchLocations
							onSelectFacility={(facility) => navigate(`/course/${facility.slug}`)}
							onSelectNearby={() => {
								navigate(`/courses/search?query=Nearby`);
							}}
							onSelectPlace={(place) => {
								const params = new URLSearchParams({
									lat: place.coordinates.latitude.toString(),
									lon: place.coordinates.longitude.toString(),
									query: place.displayValue ?? place.name,
								});
								navigate(`/courses/search?${params.toString()}`);
							}}
							onSelectRegion={(region) => {
								const params = new URLSearchParams({
									regionId: region.id,
									query: region.displayValue ?? region.name,
								});
								navigate(`/courses/search?${params.toString()}`);
							}}
							mobileInputClass="p-4"
							placeholder="Search by city or course name"
						/>
					</div>
				</div>
			</Hero>
			<Container>
				<Page>
					<Section>
						<Heading as="h2">Quick Filters</Heading>
						<div class="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2">
							<div class="relative flex items-center gap-4 rounded-lg border border-neutral p-4 text-balance transition-colors hover:border-brand">
								<div class="grid aspect-square size-18 place-items-center self-start rounded-lg bg-brand text-4xl text-white md:self-center">
									<IconAccessPointsCustom />
								</div>
								<div>
									<Heading as="h3" size="p">
										<Link href="/courses/search?accessPoints=true" class="after:absolute after:inset-0">
											Access Points
										</Link>
									</Heading>
									<p class="text-sm text-neutral-800">
										View all participating courses where you can earn and redeem Access Points.
									</p>
								</div>
								<div class="flex size-16 items-center justify-end text-lg text-brand">
									<IconArrowRightMd />
								</div>
							</div>

							<div class="relative flex items-center gap-4 rounded-lg border border-neutral p-4 text-balance transition-colors hover:border-brand">
								<div class="grid aspect-square size-18 place-items-center self-start rounded-lg bg-black text-4xl text-white md:self-center">
									<IconAccessRatesCustom />
								</div>
								<div>
									<Heading as="h3" size="p">
										<Link href="/courses/search?premiumRates=true" class="after:absolute after:inset-0">
											Access Premium Rates
										</Link>
									</Heading>
									<p class="text-sm text-neutral-800">
										View all participating courses that support exclusive tee time rates for Access Premium members.
									</p>
								</div>
								<div class="flex size-16 items-center justify-end text-lg text-brand">
									<IconArrowRightMd />
								</div>
							</div>
						</div>
					</Section>

					<HorizontalRule />

					<Section>
						<div class="flex flex-col gap-4">
							<Heading as="h2">Popular Destinations</Heading>
							<p>Find courses in your favorite destinations across the United States.</p>
						</div>

						<ul class="grid grid-cols-2 gap-4 lg:grid-cols-4">
							<For each={[...(popularDesinations.latest?.places ?? []), ...(popularDesinations.latest?.regions ?? [])]}>
								{(dest) => (
									<li>
										<PopularDestinationFullBleed destination={dest} url="/courses/search" />
									</li>
								)}
							</For>
						</ul>
					</Section>

					<HorizontalRule />

					<Section>
						<div class="flex flex-col gap-4">
							<Heading as="h2">All Courses</Heading>
							<p>
								Simply book a tee time with any of our participating courses and complete your round to start earning
								rewards.
							</p>
						</div>
						<Show when={facilities()}>{(facilities) => <FacilitiesByGrouping facilities={facilities()} />}</Show>
					</Section>
				</Page>
			</Container>
		</>
	);
}

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
const getPopular = cachedGet('/v0/search/popular-destinations');
const getFacilities = cachedGet('/v0/facilities');
